import { useState } from 'react';
import { ArrowLeft, BadgeCheck, Heart, History, Package, PanelRight, ShieldCheck, Wand2 } from 'lucide-react';
import Avatar from '../ui/Avatar';
import IconButton from '../ui/IconButton';
import StarRating from '../ui/StarRating';
import { absoluteAssetUrl } from '../../lib/chatMappers';
import { peerFact } from '../../lib/peerFacts';

/**
 * Header for the open conversation.
 *
 * Responsive shape - one row at every width, never a wrapping stack:
 *   phone   avatar + name + status line, then icon-only actions (~56px tall)
 *   tablet  the rating line and the "Chat History" label come back
 *   lg+     unchanged: full labels on the actions
 *
 * Everything here comes from the conversations payload plus an optional public
 * profile fetch. Two fields the Phase 2 spec asked for are simply not in the
 * chat payload, so they degrade honestly rather than showing invented data:
 *   - rating → only rendered when the public profile reports ratings
 *   - language → not exposed at all; the country the API does expose is
 *     deliberately NOT shown in chat (owner request), so the status line carries
 *     a public identity fact instead - see lib/peerFacts.js
 *
 * The My Orders / User Profile switch is NOT here at any width: it lives at the
 * top of the details panel itself (RightPanel), directly above the user card.
 * That keeps this row to identity + actions, so nothing has to be hidden,
 * truncated or wrapped to make room for the tabs.
 */
export default function ChatHeader({
  conversation,
  currentUser,
  profile,
  onBack,
  isFavorite,
  onToggleFavorite,
  onOpenHistory,
  historyOpen = false,
  onOpenDetails,
}) {
  const { name, username, avatarUrl, online, verified, listingTitle, officialTitle } = conversation;

  // Staff messages are answered from the same composer, so the moderator gets a
  // small set of one-click replies instead of retyping the same policy lines.
  const [toolsOpen, setToolsOpen] = useState(false);
  const isModerator = Boolean(currentUser?.isOfficialAccount);
  const QUICK_REPLIES = [
    'Hello, this is Z2Market moderation regarding your recent orders.',
    'Please deliver this order within the promised delivery time, or update the buyer about the delay.',
    'We noticed a delivery delay on this order. Please respond within 24 hours.',
    'Please do not share account credentials or delivery details outside the Z2Market chat.',
    'Your case has been forwarded to the review team. We will update you here.',
  ];
  const insertQuickReply = (text) => {
    window.dispatchEvent(new CustomEvent('z2m:insert-message', { detail: text }));
    setToolsOpen(false);
  };

  // The country is never rendered in chat. In its place: the counterparty's
  // public identity fact (seller rank, else how long they have been a member).
  // Null when neither exists, and then the slot and its separator are dropped.
  const fact = peerFact(profile);

  // The conversations payload may carry no avatar (the platform's authoritative
  // field is storeAvatarUrl, which lives on sellerProfile). The public-profile
  // lookup already made for the rating fills the gap when it is available.
  const avatarSrc = avatarUrl || absoluteAssetUrl(profile?.storeAvatarUrl || profile?.avatar);

  const rating = Number(profile?.rating ?? 0);
  const totalRatings = Number(profile?.totalRatings ?? 0);
  const hasRating = totalRatings > 0 && rating > 0;

  return (
    <header className="sticky top-0 z-20 shrink-0 border-b border-line bg-surface">
      <div className="flex min-h-[56px] items-center gap-1 px-1.5 py-1.5 sm:gap-2 sm:px-2.5 lg:gap-4 lg:px-4 lg:py-3">
        <IconButton label="Back to welcome screen" onClick={onBack} className="-ml-0.5">
          <ArrowLeft size={17} />
        </IconButton>

        <Avatar name={name} src={avatarSrc} online={online} size="header" />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[14.5px] leading-tight font-bold text-ink">{name}</span>
            {verified && (
              <BadgeCheck
                size={14}
                aria-label="Verified seller"
                className="shrink-0 text-accent"
              />
            )}
            {officialTitle && (
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-white"
                style={{ backgroundColor: '#7c3aed' }}
                title="Official Z2Market staff account"
              >
                <ShieldCheck size={11} aria-hidden="true" />
                {officialTitle}
              </span>
            )}
          </div>

          {/* Rating: tablet and up only. On a phone this line is dropped so the
              header stays two lines tall; the same rating is one tap away in the
              details panel. */}
          <div className="mt-0.5 hidden items-center gap-1.5 md:flex">
            {hasRating ? (
              <>
                <StarRating value={rating} />
                <span className="text-[11px] text-ink-3 tabular-nums">
                  {`${rating.toFixed(1)} (${totalRatings})`}
                </span>
              </>
            ) : (
              <span className="text-[11px] text-ink-3">No ratings yet</span>
            )}
          </div>

          {/* One truncating line: presence, handle, identity fact, listing context. */}
          <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden text-[11px] whitespace-nowrap text-ink-3">
            <span className="inline-flex shrink-0 items-center gap-1">
              <span
                aria-hidden="true"
                className={`h-1.5 w-1.5 rounded-full ${online ? 'bg-online' : 'bg-ink-3'}`}
              />
              {online ? 'Online' : 'Offline'}
            </span>
            {username && (
              <>
                <span aria-hidden="true" className="shrink-0">
                  ·
                </span>
                <span className="truncate">{`@${username}`}</span>
              </>
            )}
            {/* The identity fact (and its separator) only from `sm` up: a phone
                gives this line ~155px, which already fits just presence, handle
                and listing - the same reason the rating line above is `md:flex`
                only. On a phone the fact is one tap away in the details panel. */}
            {fact && (
              <span className="hidden shrink-0 items-center gap-1.5 sm:inline-flex">
                <span aria-hidden="true" className="shrink-0">
                  ·
                </span>
                <span>{fact.compact}</span>
              </span>
            )}
            {listingTitle && (
              <>
                <span aria-hidden="true" className="shrink-0">
                  ·
                </span>
                <span className="inline-flex min-w-0 items-center gap-1">
                  <Package size={11} className="shrink-0" />
                  <span className="truncate">{listingTitle}</span>
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
          {isModerator && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setToolsOpen((open) => !open)}
                aria-expanded={toolsOpen}
                aria-label="Moderator tools"
                title="Moderator tools"
                className="z2-glow-hover inline-flex h-11 items-center gap-1.5 rounded-xl border border-line px-2.5 text-[12.5px] font-semibold text-ink-2"
              >
                <Wand2 size={15} />
                <span className="hidden sm:inline">Tools</span>
              </button>
              {toolsOpen && (
                <div className="absolute right-0 z-40 mt-1 w-72 rounded-xl border border-line bg-surface p-1.5 shadow-lg">
                  <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-ink-3">
                    Quick replies
                  </p>
                  {QUICK_REPLIES.map((reply) => (
                    <button
                      key={reply}
                      type="button"
                      onClick={() => insertQuickReply(reply)}
                      className="block w-full rounded-lg px-2 py-2 text-left text-[12.5px] leading-snug text-ink-2 hover:bg-surface-3"
                    >
                      {reply}
                    </button>
                  ))}
                  <a
                    href="/admin/orders"
                    target="_blank"
                    rel="noopener"
                    className="mt-1 block rounded-lg px-2 py-2 text-[12.5px] font-semibold text-brand-700 hover:bg-surface-3"
                  >
                    Open admin orders →
                  </a>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onOpenHistory}
            aria-expanded={historyOpen}
            aria-label="Chat history"
            title="Chat history"
            className="z2-glow-hover inline-flex h-11 items-center gap-1.5 rounded-xl border border-line px-2.5 text-[12px] font-medium text-ink-2 transition hover:border-brand-300 hover:bg-brand-soft hover:text-accent lg:h-9"
          >
            <History size={15} />
            <span className="hidden sm:inline">Chat History</span>
          </button>

          <IconButton
            label={isFavorite ? 'Remove from favourites' : 'Add to favourites'}
            active={isFavorite}
            onClick={onToggleFavorite}
          >
            <Heart size={17} className={isFavorite ? 'fill-accent text-accent' : ''} />
          </IconButton>

          {/* Details live in a slide-over below lg; on lg+ the column is always there. */}
          <IconButton label="Open details" onClick={onOpenDetails} className="lg:hidden">
            <PanelRight size={17} />
          </IconButton>
        </div>
      </div>
    </header>
  );
}
